import type { LoaderFunction } from "react-router";

/**
 * Run several loaders on one route.
 *
 * React Router allows a route exactly one `loader`, but a route can have more
 * than one thing to fetch before it renders — a list page warms both its table
 * preferences and, when the URL names one, the View it should open with. The
 * two know nothing about each other and neither should learn.
 *
 * Runs them **concurrently**: they are independent fetches, and a route that
 * blocks on both should wait the longer of the two rather than their sum.
 *
 * A loader that returns a `Response` — the shape `redirect()` produces — wins,
 * and the first such loader in argument order is the one returned, so
 * precedence is something the caller can read off the call site. Otherwise
 * resolves `null`: these loaders exist to fill caches that hooks read, not to
 * supply `useLoaderData`, and merging their return values would invent a
 * contract none of them has.
 *
 * A rejection propagates, exactly as it would from a lone loader. Loaders that
 * must not fail the route are responsible for their own failures — see
 * `makeViewsLoader`, which swallows an unresolvable View so a stale link
 * cannot replace the list with an error boundary.
 */
export function composeLoaders(
  ...loaders: readonly LoaderFunction[]
): LoaderFunction {
  return async function composedLoader(args) {
    const results = await Promise.all(loaders.map((loader) => loader(args)));
    return results.find((result) => result instanceof Response) ?? null;
  };
}
